import React, { useState, useEffect } from 'react';
import { isEmptyValue, hasAnyData } from '../../utils/profileVisibility';
import SectionEditModal from './SectionEditModal';
import EditIconButton from './EditIconButton';

/**
 * PlatformPerformanceSection - Displays and allows editing of platform performance metrics
 * Shows stats only for platforms selected in Identity section
 */
function normTikTokHandleFromLink(link) {
  if (!link || typeof link !== 'string') return '';
  const t = link.trim().toLowerCase();
  if (t.includes('tiktok.com')) {
    const m = t.match(/@([^/?#]+)/);
    return m ? m[1] : t.replace(/^@/, '');
  }
  return t.replace(/^@/, '');
}

function normInstagramHandleFromLink(link) {
  if (!link || typeof link !== 'string') return '';
  const t = link.trim().toLowerCase();
  if (t.includes('instagram.com')) {
    const m = t.match(/instagram\.com\/([^/?#]+)/);
    return m ? m[1] : t.replace(/^@/, '');
  }
  return t.replace(/^@/, '');
}

function PlatformPerformanceSection({
  platformStats,
  primaryPlatforms = [], // Array of platform keys like ['tiktok', 'instagram', 'youtube']
  isOwner,
  onSavePlatformStats,
  mode = 'edit', // 'public' | 'edit'
  socialAccounts, // Optional: auto-populated TikTok/IG data (socialAccounts.tiktok.derived, etc.)
  socialLinks, // Optional: current social links from profile (to detect pending refresh when handle differs)
}) {
  const safeStats = platformStats || {};
  const safeSocial = socialAccounts || {};
  const safeLinks = socialLinks || {};
  // Resolve TikTok/Instagram URL from object { tiktok: url } or array [{ platform, url }]
  const tiktokLinkUrl = safeLinks.tiktok ?? (Array.isArray(safeLinks) ? safeLinks.find((l) => l?.platform === 'tiktok')?.url : undefined);
  const instagramLinkUrl = safeLinks.instagram ?? (Array.isArray(safeLinks) ? safeLinks.find((l) => l?.platform === 'instagram')?.url : undefined);
  
  const PLATFORM_LABELS = {
    tiktok: 'TikTok',
    instagram: 'Instagram',
    youtube: 'YouTube',
    twitter: 'Twitter / X',
    snapchat: 'Snapchat',
    facebook: 'Facebook',
    pinterest: 'Pinterest',
    linkedin: 'LinkedIn',
    twitch: 'Twitch',
  };

  // YouTube hidden from default until metrics API is available
  const DEFAULT_PLATFORMS = ['tiktok', 'instagram'];

  // Use all platforms from primaryPlatforms, or fall back to default
  let activePlatformKeys =
    primaryPlatforms && primaryPlatforms.length > 0
      ? primaryPlatforms
      : DEFAULT_PLATFORMS;

  // Optional safety: if somehow primaryPlatforms are empty after normalization, still fall back
  if (!activePlatformKeys || activePlatformKeys.length === 0) {
    activePlatformKeys = DEFAULT_PLATFORMS;
  }

  // Safety check: ensure activePlatformKeys is always an array
  if (!Array.isArray(activePlatformKeys)) {
    console.warn('PlatformPerformanceSection: activePlatformKeys is not an array', activePlatformKeys);
    return null;
  }

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formState, setFormState] = useState(() => {
    const initial = {};
    activePlatformKeys.forEach((key) => {
      const stats = safeStats[key] || {};
      initial[key] = {
        followers: stats?.followers != null ? String(stats.followers) : '',
      };
    });
    return initial;
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Sync formState when modal opens or platformStats/platforms change
  useEffect(() => {
    if (!activePlatformKeys || activePlatformKeys.length === 0) return;
    
    if (isModalOpen) {
      const updated = {};
      activePlatformKeys.forEach((key) => {
        const stats = (platformStats && platformStats[key]) || {};
        updated[key] = {
          followers: stats?.followers != null ? String(stats.followers) : '',
        };
      });
      setFormState(updated);
      setErrors({});
    }
  }, [isModalOpen, platformStats, activePlatformKeys.join(',')]);

  const handleSave = async () => {
    const newErrors = {};
    const cleanedStats = {};

    activePlatformKeys.forEach((key) => {
      // TikTok is auto-populated from Apify; do not save manual edits
      if (key === 'tiktok') return;

      const f = formState[key];
      if (!f) return;

      const parseField = (value) => {
        if (value === '' || value === null || typeof value === 'undefined') return undefined;
        if (Number.isNaN(Number(value)) || Number(value) < 0) return 'invalid';
        return Number(value);
      };

      const followers = parseField(f.followers);

      const platformErrorMessages = [];
      const checkNumber = (fieldName, val) => {
        if (val === 'invalid') {
          platformErrorMessages.push(`${fieldName} must be a non-negative number or left blank.`);
        }
      };

      checkNumber('Followers', followers);

      if (platformErrorMessages.length > 0) {
        newErrors[key] = platformErrorMessages.join(' ');
      } else {
        const platformStat = {};
        if (followers !== undefined) platformStat.followers = followers;
        if (Object.keys(platformStat).length > 0) {
          cleanedStats[key] = platformStat;
        }
      }
    });

    setErrors(newErrors);

    const hasErrors = Object.keys(newErrors).length > 0;
    if (hasErrors) return;

    try {
      setIsSaving(true);
      await onSavePlatformStats(cleanedStats);
      setIsModalOpen(false);
      setErrors({});
    } catch (err) {
      console.error('Error saving platform stats:', err);
      setErrors({ _general: `Failed to save platform stats: ${err.message || err}. Please try again.` });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setErrors({});
    // Reset form to current props
    const reset = {};
    activePlatformKeys.forEach((key) => {
      const stats = (platformStats && platformStats[key]) || {};
      reset[key] = {
        followers: stats?.followers != null ? String(stats.followers) : '',
      };
    });
    setFormState(reset);
  };

  // View mode
  const renderViewMode = () => {
    // Build metrics list for each platform and filter empty ones
    const platformCards = activePlatformKeys.map((key) => {
      const stats = (platformStats && platformStats[key]) || {};
      const tiktokDerived = key === 'tiktok' ? safeSocial.tiktok?.derived : null;
      const tiktokFollowers = key === 'tiktok' ? safeSocial.tiktok?.followersCount : undefined;
      const rawStored = key === 'tiktok' ? (safeSocial.tiktok?.handle ?? '') : '';
      const tiktokStoredHandle = key === 'tiktok' ? String(rawStored).replace(/^@/, '').trim().toLowerCase() : '';
      const tiktokCurrentHandle = key === 'tiktok' ? normTikTokHandleFromLink(tiktokLinkUrl) : '';
      // Only show "Pending refresh" when we have no derived data yet or the link handle changed. Do NOT hide
      // existing data just because handleLastChangedAt > lastRefreshedAt (e.g. refresh failed after link save).
      const tiktokPendingRefresh = key === 'tiktok' && tiktokLinkUrl && tiktokCurrentHandle && (!tiktokDerived || tiktokStoredHandle !== tiktokCurrentHandle);

      // Instagram: pending if pendingRefresh flag or link handle differs from stored
      const igLink = instagramLinkUrl;
      const igCurrentHandle = key === 'instagram' ? normInstagramHandleFromLink(igLink) : '';
      const igStoredHandle = key === 'instagram' ? String(safeSocial.instagram?.handle ?? '').replace(/^@/, '').trim().toLowerCase() : '';
      const instagramPendingRefresh =
        key === 'instagram' &&
        (safeSocial.instagram?.pendingRefresh === true ||
          (igLink && igCurrentHandle && igStoredHandle && igCurrentHandle !== igStoredHandle));

      const igDerived = key === 'instagram' ? safeSocial.instagram?.derived : null;
      const igFollowers = key === 'instagram' ? safeSocial.instagram?.followersCount : undefined;

      // Resolve performance stability: prefer new label, else map old consistencyLabel
      const perfStabilityValue =
        key === 'tiktok'
          ? (tiktokDerived?.performanceStabilityLabel != null
              ? tiktokDerived.performanceStabilityLabel
              : tiktokDerived?.consistencyLabel != null
                ? { consistent: 'stable', mixed: 'variable', volatile: 'spiky' }[String(tiktokDerived.consistencyLabel).toLowerCase()] ?? tiktokDerived.consistencyLabel
                : null)
          : key === 'instagram' && igDerived?.performanceStabilityLabel != null
          ? igDerived.performanceStabilityLabel
          : null;

      const igStabilityLabel =
        key === 'instagram' && igDerived
          ? igDerived.stabilitySeriesUsed === 'views'
            ? 'Views stability'
            : igDerived.stabilitySeriesUsed === 'engagement'
            ? 'Engagement stability'
            : 'Stability'
          : null;

      // TikTok: always prefer Apify data (socialAccounts.tiktok) over manual platformStats
      let metrics;
      if (key === 'tiktok' && tiktokDerived && !tiktokPendingRefresh) {
        metrics = [
          { label: 'Followers', value: tiktokFollowers ?? stats.followers },
          { label: 'Avg views (last 10)', value: tiktokDerived.avgViews10 },
          { label: 'Median views (last 10)', value: tiktokDerived.medianViews10 },
          { label: 'ER (followers median)', value: tiktokDerived.erFollowersMedian },
          { label: 'Performance stability', value: perfStabilityValue, tooltip: 'Based on how tightly views cluster across the last 10 posts. Stable = more predictable reach.' },
        ];
      } else if (key === 'tiktok' && tiktokPendingRefresh) {
        metrics = [
          { label: 'Followers', value: null },
          { label: 'Avg views (last 10)', value: null },
          { label: 'Median views (last 10)', value: null },
          { label: 'ER (followers median)', value: null },
          { label: 'Performance stability', value: null },
        ];
      } else if (key === 'tiktok') {
        metrics = [
          { label: 'Followers', value: tiktokFollowers ?? stats.followers },
          { label: 'Avg views (last 10)', value: tiktokDerived?.avgViews10 },
          { label: 'Median views (last 10)', value: tiktokDerived?.medianViews10 },
          { label: 'ER (followers median)', value: tiktokDerived?.erFollowersMedian },
          { label: 'Performance stability', value: perfStabilityValue, tooltip: 'Based on how tightly views cluster across the last 10 posts. Stable = more predictable reach.' },
        ];
      } else if (key === 'instagram' && igDerived && !instagramPendingRefresh) {
        const medianLabel =
          igDerived.medianViews10 != null ? 'Median views (last 10)' : 'Median engagement (last 10)';
        const medianValue = igDerived.medianViews10 ?? igDerived.medianEngagement10 ?? null;
        metrics = [
          { label: 'Followers', value: igFollowers ?? stats.followers },
          { label: medianLabel, value: medianValue },
          { label: 'Median ER by followers (last 10)', value: igDerived.erFollowersMedian },
          { label: igStabilityLabel || 'Stability', value: perfStabilityValue, tooltip: igStabilityLabel ? `${igStabilityLabel}: ${perfStabilityValue ?? '—'}` : undefined },
        ];
      } else if (key === 'instagram' && instagramPendingRefresh) {
        metrics = [
          { label: 'Followers', value: null },
          { label: 'Median views (last 10)', value: null },
          { label: 'Median ER by followers (last 10)', value: null },
          { label: 'Stability', value: null },
        ];
      } else if (key === 'instagram') {
        const medianLabel =
          igDerived?.medianViews10 != null ? 'Median views (last 10)' : 'Median engagement (last 10)';
        const medianValue = igDerived?.medianViews10 ?? igDerived?.medianEngagement10 ?? null;
        metrics = [
          { label: 'Followers', value: igFollowers ?? stats.followers },
          { label: medianLabel, value: medianValue },
          { label: 'Median ER by followers (last 10)', value: igDerived?.erFollowersMedian },
          { label: igStabilityLabel || 'Stability', value: perfStabilityValue },
        ];
      } else {
        metrics = [
          { label: 'Followers', value: stats.followers },
        ];
      }

      // Filter out empty metrics (public mode only), but treat pending TikTok/IG as having "data" so we show the card with pending message
      const pendingRefresh = tiktokPendingRefresh || instagramPendingRefresh;
      const visibleMetrics = mode === 'public' && !pendingRefresh
        ? metrics.filter((m) => !isEmptyValue(m.value))
        : metrics;

      return {
        key,
        stats,
        visibleMetrics,
        hasData: visibleMetrics.length > 0 || pendingRefresh,
        tiktokPendingRefresh: key === 'tiktok' && tiktokPendingRefresh,
        instagramPendingRefresh: key === 'instagram' && instagramPendingRefresh,
      };
    });

    // Filter out empty platform cards (public mode only)
    const visiblePlatformCards = mode === 'public'
      ? platformCards.filter((card) => card.hasData)
      : platformCards;

    // Hide entire section if no platforms have data (public mode only)
    if (mode === 'public' && visiblePlatformCards.length === 0) {
      return null;
    }

    return (
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 md:p-6 mb-5 md:mb-6 relative">
        <div className="mb-3 md:mb-4 flex items-center justify-between gap-2">
          <h2 className="text-lg md:text-xl font-semibold text-slate-900">
            Platform Performance
          </h2>
        </div>
        {isOwner && (
          <div className="absolute right-4 sm:right-6 top-4 sm:top-6 z-10">
            <EditIconButton onClick={() => setIsModalOpen(true)} />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {visiblePlatformCards.map((card) => {
            const { key, stats, visibleMetrics, tiktokPendingRefresh, instagramPendingRefresh } = card;
            const pendingRefresh = tiktokPendingRefresh || instagramPendingRefresh;

            return (
              <div key={key} className="rounded-md bg-slate-50 border border-slate-200 px-3 py-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                  {PLATFORM_LABELS[key] || key}
                </div>
                <div className="space-y-1.5 text-xs text-slate-600">
                  {visibleMetrics.map((metric) => (
                    <div key={metric.label} className="flex justify-between gap-2">
                      <span title={metric.tooltip || undefined}>{metric.label}</span>
                      <span className="font-semibold text-slate-900">
                        {typeof metric.value === 'number'
                          ? metric.label === 'ER (followers median)' || metric.label === 'Median ER by followers (last 10)'
                            ? `${(metric.value * 100).toFixed(2)}%`
                            : metric.value.toLocaleString()
                          : typeof metric.value === 'string'
                          ? metric.value
                          : '—'}
                      </span>
                    </div>
                  ))}
                </div>
                {pendingRefresh && (
                  <p className="mt-2 text-xs text-slate-500 italic">Pending refresh</p>
                )}
              </div>
            );
          })}
        </div>
        {mode === 'edit' && visiblePlatformCards.length === 0 && (
          <p className="text-sm text-slate-500 mt-4">
            No performance data added yet.
          </p>
        )}
      </section>
    );
  };

  return (
    <>
      {renderViewMode()}
      
      {isOwner && (
        <SectionEditModal
          isOpen={isModalOpen}
          onClose={handleCancel}
          title="Edit Platform Performance"
          subtitle="Update your follower counts and key performance stats."
          onSave={handleSave}
          isSaving={isSaving}
          errors={errors}
        >
          <div className="space-y-4">
        {activePlatformKeys.map((key) => {
          const platformForm = formState[key] || { followers: '' };
          const isTiktokAuto = key === 'tiktok';
          const isInstagramAuto = key === 'instagram';
          const tiktokFollowers = isTiktokAuto ? safeSocial.tiktok?.followersCount : null;
          const tiktokHasData = isTiktokAuto && (tiktokFollowers != null || safeSocial.tiktok?.derived);
          const igFollowers = isInstagramAuto ? safeSocial.instagram?.followersCount : null;
          const igHasData = isInstagramAuto && (igFollowers != null || safeSocial.instagram?.derived);

          return (
            <div key={key} className="border border-slate-200 rounded-md p-3 md:p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                {PLATFORM_LABELS[key] || key}
              </h3>
              {isTiktokAuto ? (
                <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-600">
                  <p className="font-medium text-slate-700">Auto-populated from TikTok</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Follower count and performance stats are updated automatically when you add or change your TikTok link in the Identity section. There is nothing to edit here.
                  </p>
                  {tiktokHasData && (
                    <p className="mt-2 text-slate-700">
                      <span className="font-medium">Current followers:</span>{' '}
                      {tiktokFollowers != null ? tiktokFollowers.toLocaleString() : '—'}
                    </p>
                  )}
                </div>
              ) : isInstagramAuto ? (
                <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-600">
                  <p className="font-medium text-slate-700">Auto-populated from Instagram</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Follower count and performance stats are updated when you add or change your Instagram link in the Identity section and trigger a refresh. There is nothing to edit here.
                  </p>
                  {igHasData && (
                    <p className="mt-2 text-slate-700">
                      <span className="font-medium">Current followers:</span>{' '}
                      {igFollowers != null ? igFollowers.toLocaleString() : '—'}
                    </p>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Followers</label>
                    <input
                      type="number"
                      min={0}
                      className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent"
                      value={platformForm?.followers ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormState((prev) => ({
                          ...prev,
                          [key]: {
                            ...prev[key],
                            followers: value,
                          },
                        }));
                      }}
                    />
                  </div>
                </div>
              )}
              {errors[key] && (
                <p className="mt-2 text-xs text-red-500">{errors[key]}</p>
              )}
            </div>
          );
        })}

          </div>
        </SectionEditModal>
      )}
    </>
  );
}

export default PlatformPerformanceSection;
